import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Select, InputNumber, Input, Button, Spin, Tabs, Tag, message, Empty,
  Alert, Progress, Switch, Popconfirm, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined,
  SaveOutlined, ArrowLeftOutlined, WarningFilled, CheckCircleFilled,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { fetchProducts, fetchRequirement, fetchPlans, fetchPlanDetail, createPlan, deletePlan } from './api';

/* ================================================================== *
 *  Tokens
 * ================================================================== */
const C = {
  ink: '#16233d', sub: '#64748b', mute: '#94a3b8', line: '#e2e8f0',
  bg: '#f1f5f9', white: '#fff',
  red: '#dc2626', redBg: '#fef2f2', green: '#059669', greenBg: '#ecfdf5',
  blue: '#2563eb', amber: '#b45309',
};

const S = {
  page: { padding: '14px 16px', backgroundColor: C.bg, minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" },
  card: { backgroundColor: C.white, borderRadius: 8, border: `1px solid ${C.line}`, boxShadow: '0 1px 2px rgba(15,23,42,.05)' },
  pad: { padding: '12px 14px' },
  h1: { fontSize: 17, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: -0.2 },
  sub: { fontSize: 11.5, color: C.sub, marginTop: 1 },
  lbl: { fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mute, display: 'block', marginBottom: 3 },
  mono: { fontFamily: "'JetBrains Mono', ui-monospace, monospace" },
  hint: { fontSize: 11, color: C.sub },
};

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

/**
 * Merge per-product rows into one procurement list.
 * Required ADDS UP across products; stock does NOT - it is the same physical
 * stock seen twice, so summing it would hide real shortages.
 */
const mergeMaterials = (lists) => {
  const by = new Map();
  lists.forEach((rows) => (rows || []).forEach((m) => {
    const k = m.material_code || `id:${m.material_id}`;
    if (!by.has(k)) by.set(k, { ...m, required_qty: 0, _stdSet: new Set() });
    const t = by.get(k);
    t.required_qty = num(t.required_qty) + num(m.required_qty);
    // Std/batch only means something if every product using this material
    // specifies the same figure; otherwise there is no single value to show.
    t._stdSet.add(num(m.bmr_standard_qty));
    t.has_invalid_std_qty = t.has_invalid_std_qty || m.has_invalid_std_qty;
    ['total_stock_qty', 'approved_qty', 'quarantine_qty', 'under_sampling_qty', 'under_test_qty', 'expired_qty', 'other_qty']
      .forEach((key) => { t[key] = num(m[key]); });
  }));
  return [...by.values()].map(({ _stdSet, ...m }) => ({
    ...m,
    bmr_standard_qty: _stdSet.size === 1 ? [..._stdSet][0] : null,
    short_qty: Math.max(0, num(m.required_qty) - num(m.approved_qty)),
  }));
};

const sortShortFirst = (rows = []) => [...rows].sort(
  (a, b) => num(b.short_qty) - num(a.short_qty) || String(a.material_code || '').localeCompare(String(b.material_code || ''))
);

const exportMaterials = (rows, filename) => {
  if (!rows || rows.length === 0) { message.warning('Nothing to export'); return; }
  const sheet = XLSX.utils.json_to_sheet(sortShortFirst(rows).map((r) => ({
    'Material Code': r.material_code, 'Material Name': r.material_name, UOM: r.uom,
    'Std/Batch': r.bmr_standard_qty === null ? '' : num(r.bmr_standard_qty),
    Required: num(r.required_qty),
    'Total Stock': num(r.total_stock_qty),
    Approved: num(r.approved_qty),
    Quarantine: num(r.quarantine_qty), 'Under Sampling': num(r.under_sampling_qty),
    'Under Test': num(r.under_test_qty), Expired: num(r.expired_qty),
    'Other (unrecognised QC status)': num(r.other_qty),
    Short: num(r.short_qty),
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Material Requirement');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/* ================================================================== *
 *  Status strip — verdict and figures on ONE line, not five big tiles
 * ================================================================== */
const Stat = ({ label, value, color }) => {
  // Batch sizes are long strings ("1,01,880 Tablets (10.57 Kg)"), so the type
  // steps down rather than blowing the strip apart.
  const len = String(value ?? '').length;
  return (
    <div style={{ padding: '0 14px', borderLeft: `1px solid ${C.line}`, lineHeight: 1.15, maxWidth: 210 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mute, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: len > 18 ? 11 : len > 10 ? 13 : 16, fontWeight: 700, color: color || C.ink, ...S.mono }}>{value}</div>
    </div>
  );
};

const StatusStrip = ({ materials, batches, products, batchesPossible, batchSize }) => {
  const short = (materials || []).filter((m) => num(m.short_qty) > 0);
  const ok = short.length === 0 && (materials || []).length > 0;
  return (
    <div style={{
      ...S.card, marginBottom: 10, padding: '9px 14px',
      backgroundColor: ok ? C.greenBg : (short.length ? C.redBg : C.white),
      borderColor: ok ? '#a7f3d0' : (short.length ? '#fecaca' : C.line),
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 320px', minWidth: 260 }}>
        {ok ? <CheckCircleFilled style={{ color: C.green, fontSize: 16 }} />
          : <WarningFilled style={{ color: short.length ? C.red : C.mute, fontSize: 16 }} />}
        <div style={{ fontSize: 13, fontWeight: 700, color: ok ? C.green : (short.length ? C.red : C.sub) }}>
          {ok ? `All materials covered — you can run ${batches} batch${batches === 1 ? '' : 'es'}`
            : short.length ? `${short.length} material${short.length === 1 ? '' : 's'} short for ${batches} batch${batches === 1 ? '' : 'es'}`
              : 'Nothing to calculate'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Stat label="Products" value={products} />
        <Stat label="Batches" value={batches} />
        {batchSize && <Stat label="Theoretical Batch Size" value={batchSize} />}
        <Stat label="Materials" value={(materials || []).length} />
        <Stat label="Short" value={short.length} color={short.length ? C.red : C.green} />
        <Stat label="Can Run" value={batchesPossible} color={C.green} />
      </div>
    </div>
  );
};

/** The procurement answer, only when there is one to give. */
const ShortList = ({ materials }) => {
  const short = (materials || []).filter((m) => num(m.short_qty) > 0);
  if (!short.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.red, alignSelf: 'center', marginRight: 2 }}>PROCURE:</span>
      {short.map((m) => (
        <span key={m.material_code} style={{
          backgroundColor: C.white, border: '1px solid #fecaca', borderRadius: 5, padding: '2px 8px', fontSize: 11.5,
        }}>
          <span style={{ ...S.mono, fontWeight: 700, color: C.ink }}>{m.material_code || '—'}</span>
          <span style={{ color: C.red, fontWeight: 700, marginLeft: 6 }}>+{fmt(m.short_qty)}</span>
          <span style={{ color: C.mute, marginLeft: 3 }}>{m.uom || ''}</span>
        </span>
      ))}
    </div>
  );
};

const Warnings = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Alert type="warning" showIcon banner style={{ marginBottom: 10, fontSize: 12, padding: '6px 12px' }}
      message={<span style={{ fontSize: 12 }}>{items.join(' · ')}</span>} />
  );
};

/* ================================================================== *
 *  Material table — dense
 * ================================================================== */
const MaterialTable = ({ rows, loading, emptyText }) => {
  const [shortOnly, setShortOnly] = useState(false);
  const data = useMemo(() => {
    const base = sortShortFirst(rows || []);
    return (shortOnly ? base.filter((m) => num(m.short_qty) > 0) : base).map((r, i) => ({ ...r, key: r.material_code || i }));
  }, [rows, shortOnly]);
  const shortCount = (rows || []).filter((m) => num(m.short_qty) > 0).length;

  // A real 0.000 is printed rather than a dash, so an empty bucket cannot be
  // mistaken for data that failed to load. Only a genuinely absent value
  // (null/undefined) shows a dash.
  const q = (title, key, color, bold, tip) => ({
    title: tip ? <Tooltip title={tip}><span style={{ borderBottom: '1px dotted #94a3b8', cursor: 'help' }}>{title}</span></Tooltip> : title,
    dataIndex: key, key, width: 96, align: 'right',
    sorter: (a, b) => num(a[key]) - num(b[key]),
    render: (v) => {
      if (v === null || v === undefined) return <span style={{ color: C.mute }}>—</span>;
      const z = num(v) === 0;
      return <span style={{ ...S.mono, fontSize: 11.5, color: z ? '#cbd5e1' : (color || '#334155'), fontWeight: bold && !z ? 700 : 400 }}>{fmt(v)}</span>;
    },
  });

  const columns = [
    {
      title: 'Code', dataIndex: 'material_code', key: 'material_code', width: 124, fixed: 'left',
      sorter: (a, b) => String(a.material_code || '').localeCompare(String(b.material_code || '')),
      render: (v) => <span style={{ ...S.mono, fontSize: 11.5, fontWeight: 600, color: C.ink }}>{v || '—'}</span>,
    },
    {
      title: 'Material Name', dataIndex: 'material_name', key: 'material_name', ellipsis: true,
      render: (v, r) => (
        <Tooltip title={v}>
          <span style={{ fontSize: 12 }}>
            {v || '—'}
            {r.has_invalid_std_qty && (
              <Tooltip title="This BOM line has no readable standard quantity — it was treated as 0 and cannot be planned against.">
                <WarningFilled style={{ color: C.amber, fontSize: 11, marginLeft: 5 }} />
              </Tooltip>
            )}
          </span>
        </Tooltip>
      ),
    },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 58, align: 'center', render: (v) => <span style={{ fontSize: 10.5, color: C.sub }}>{v || '—'}</span> },
    q('Std/Batch', 'bmr_standard_qty', null, false, 'Standard quantity for ONE batch (bmr_standard_qty). Blank on the combined view when products specify different figures for the same material.'),
    q('Required', 'required_qty', C.ink, true, 'Std/Batch × number of batches.'),
    q('Total Stock', 'total_stock_qty', C.sub, false, 'Every QC bucket added together — all the material that physically exists. Shown for context only; shortage is measured against Approved alone.'),
    q('Approved', 'approved_qty', C.green, true, 'QC-approved stock. This is the ONLY stock counted as available.'),
    {
      title: <Tooltip title="Shown for visibility only. NOT counted as available — only QC-approved stock can go into production.">
        <span style={{ borderBottom: '1px dotted #94a3b8', cursor: 'help', fontSize: 11 }}>Other stock (not counted)</span></Tooltip>,
      key: 'other',
      children: [
        q('Quar.', 'quarantine_qty', C.amber),
        q('U.Samp', 'under_sampling_qty', C.sub),
        q('U.Test', 'under_test_qty', C.sub),
        q('Expired', 'expired_qty', C.mute),
        q('Other', 'other_qty', C.sub, false, 'Stock under a QC status this system does not recognise (e.g. OnHold, Partially ID Approved, Rejected, Modification Pending). Never counted as available.'),
      ],
    },
    {
      title: 'Short', dataIndex: 'short_qty', key: 'short_qty', width: 104, align: 'right', fixed: 'right',
      defaultSortOrder: 'descend', sorter: (a, b) => num(a.short_qty) - num(b.short_qty),
      render: (v) => (num(v) > 0
        ? <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{fmt(v)}</span>
        : <CheckCircleFilled style={{ color: '#6ee7b7', fontSize: 12 }} />),
    },
  ];

  return (
    <>
      {shortCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
          <Switch size="small" checked={shortOnly} onChange={setShortOnly} />
          <span style={{ fontSize: 11.5, color: C.sub }}>Show only the {shortCount} short material{shortCount === 1 ? '' : 's'}</span>
        </div>
      )}
      <Table
        columns={columns} dataSource={data} loading={loading} bordered size="small"
        pagination={data.length > 40 ? { pageSize: 40, showSizeChanger: true, pageSizeOptions: ['20', '40', '100'], size: 'small', showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` } : false}
        scroll={{ x: 'max-content' }} sticky={{ offsetHeader: 0 }}
        rowClassName={(r) => (num(r.short_qty) > 0 ? 'mm-short' : '')}
        locale={{ emptyText: <div style={{ padding: '18px 0' }}><Empty description={<span style={{ fontSize: 12 }}>{emptyText || 'No materials'}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div> }}
      />
      <style>{`
        .mm-short > td { background-color: ${C.redBg} !important; }
        .mm-short:hover > td { background-color: #fee2e2 !important; }
        .mm-tight .ant-table-thead > tr > th { padding: 6px 8px !important; font-size: 11px !important; }
        .mm-tight .ant-table-tbody > tr > td { padding: 5px 8px !important; }
        .mm-tight .ant-tabs-nav { margin-bottom: 10px !important; }
      `}</style>
    </>
  );
};

/* ================================================================== *
 *  Module
 * ================================================================== */
const MaterialManagement = () => {
  // Saved Plans is the landing view - opening the module usually means
  // checking on work already planned, not starting something new.
  const [view, setView] = useState('list');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [items, setItems] = useState([{ key: 1, family: null, product_id: null, batches: 1 }]);
  const [planName, setPlanName] = useState('');
  const [results, setResults] = useState({});
  const [reqLoading, setReqLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try { setProducts(await fetchProducts()); }
    catch (e) { message.error(e.message || 'Could not load products'); }
    finally { setProductsLoading(false); }
  }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  // The same drug appears many times - once per BMR/BPR document and once per
  // revision of it - so the list is grouped by product family (is_product, e.g.
  // ATS = Atorvastatin) and the document code is part of each label. Flat and
  // unlabelled, the entries look like meaningless duplicates.
  /* Two-step product picker.
   *
   * Step 1 is the product family (product_details.is_product, e.g. ATS =
   * Atorvastatin). Step 2 is the specific BMR/BPR document within it. A drug
   * carries one entry per document AND one per revision of it - 71 rows for
   * Atorvastatin alone - so a single flat list is unusable.
   *
   * Note this vocabulary is is_product, NOT the short codes on the Create Plan
   * screen (which come from product_option_volume_masters and use ATR where
   * this uses ATS). The two share only FAMO and FURO, and nothing maps between
   * them, so they cannot be made to agree without a new mapping table.
   */
  const familyOptions = useMemo(() => {
    const g = new Map();
    products.forEach((p) => {
      const fam = p.product_family || null;
      if (!fam) return;                       // 639 unclassified rows, none have a BOM
      if (!g.has(fam)) g.set(fam, { total: 0, usable: 0 });
      const e = g.get(fam);
      e.total += 1;
      if (p.has_bom !== false) e.usable += 1;
    });
    return [...g.entries()]
      .filter(([, e]) => e.usable > 0)        // a family with no BOM anywhere is a dead end
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([fam, e]) => ({
        value: fam,
        label: `${fam}  (${e.usable})`,
        search: fam.toLowerCase(),
      }));
  }, [products]);

  const docOptionsFor = useCallback((family) => {
    if (!family) return [];
    return products
      .filter((p) => (p.product_family || null) === family)
      .map((p) => ({
        value: p.id,
        disabled: p.has_bom === false,
        label: [
          (p.bmr_bpr_code && p.bmr_bpr_code !== 'N/A') ? p.bmr_bpr_code : p.name,
          p.bmr_bpr_type ? `· ${p.bmr_bpr_type}` : null,
          p.has_bom === false ? '· no BOM' : `· ${p.bom_material_count} materials`,
          `· id ${p.id}`,
        ].filter(Boolean).join(' '),
        search: `${p.id} ${p.name} ${p.bmr_bpr_code || ''}`.toLowerCase(),
      }));
  }, [products]);

  const filterProduct = useCallback(
    (input, option) => (option?.search || '').includes(String(input).toLowerCase().trim()),
    []
  );
  const productById = useCallback((id) => products.find((p) => p.id === id) || null, [products]);

  const validItems = useMemo(() => items.filter((i) => i.product_id && i.batches >= 1), [items]);

  const loadRequirements = useCallback(async () => {
    if (!validItems.length) { setResults({}); return; }
    setReqLoading(true);
    try {
      const out = {};
      for (const it of validItems) out[it.product_id] = await fetchRequirement({ product_id: it.product_id, batches: it.batches });
      setResults(out);
    } catch (e) { message.error(e.message || 'Could not calculate requirement'); }
    finally { setReqLoading(false); }
  }, [validItems]);
  useEffect(() => { loadRequirements(); }, [loadRequirements]);

  const combined = useMemo(() => mergeMaterials(validItems.map((i) => results[i.product_id]?.materials)), [validItems, results]);
  const totalBatches = useMemo(() => validItems.reduce((s, i) => s + i.batches, 0), [validItems]);
  const allWarnings = useMemo(() => {
    const seen = new Set();
    validItems.forEach((i) => (results[i.product_id]?.warnings || []).forEach((w) => seen.add(w)));
    return [...seen];
  }, [validItems, results]);
  const batchesPossible = useMemo(() => {
    const v = validItems.map((i) => results[i.product_id]?.summary?.batches_possible).filter((x) => x !== undefined);
    return v.length ? Math.min(...v) : '—';
  }, [validItems, results]);

  const addItem = () => setItems((p) => [...p, { key: Date.now(), family: null, product_id: null, batches: 1 }]);
  const removeItem = (k) => setItems((p) => (p.length === 1 ? p : p.filter((i) => i.key !== k)));
  const patchItem = (k, patch) => setItems((p) => p.map((i) => (i.key === k ? { ...i, ...patch } : i)));

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try { setPlans(await fetchPlans()); }
    catch (e) { message.error(e.message || 'Could not load plans'); }
    finally { setPlansLoading(false); }
  }, []);

  const openPlan = useCallback(async (id) => {
    setView('detail'); setDetailLoading(true);
    try { setDetail(await fetchPlanDetail(id)); }
    catch (e) { message.error(e.message || 'Could not load plan'); setDetail(null); }
    finally { setDetailLoading(false); }
  }, []);

  const handleSave = useCallback(async () => {
    if (!validItems.length) { message.warning('Pick at least one product'); return; }
    setSaving(true);
    try {
      const made = await createPlan({ plan_name: planName || null, items: validItems.map((i) => ({ product_id: i.product_id, no_of_batches: i.batches })) });
      message.success(`Plan created — ${made.rows_created} batches`);
      setPlanName(''); openPlan(made.plan_id);
    } catch (e) { message.error(e.message || 'Could not create plan'); }
    finally { setSaving(false); }
  }, [validItems, planName, openPlan]);

  const handleDelete = useCallback(async (id) => {
    try { await deletePlan(id); message.success('Plan deleted'); loadPlans(); }
    catch (e) { message.error(e.message || 'Could not delete plan'); }
  }, [loadPlans]);

  useEffect(() => { if (view === 'list') loadPlans(); }, [view, loadPlans]);

  const planColumns = [
    {
      title: 'Plan', dataIndex: 'plan_id', key: 'plan_id', width: 210,
      render: (v, r) => (
        <>
          <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 700, fontSize: 12.5 }} onClick={() => openPlan(v)}>
            {r.plan_name || 'Untitled plan'}
          </Button>
          <div style={{ ...S.mono, fontSize: 10, color: C.mute }}>{v}</div>
        </>
      ),
    },
    {
      title: 'Products', dataIndex: 'product_list', key: 'product_list',
      render: (list = []) => (
        <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {list.slice(0, 2).map((p, i) => <div key={i}>{p.name} <span style={{ color: C.mute }}>× {p.batches}</span></div>)}
          {list.length > 2 && <span style={{ color: C.mute }}>+{list.length - 2} more</span>}
        </div>
      ),
    },
    { title: 'Batches', dataIndex: 'total_batches', key: 'total_batches', width: 72, align: 'center', render: (v) => <span style={{ ...S.mono, fontWeight: 700 }}>{v}</span> },
    {
      title: 'Progress', key: 'progress', width: 160,
      render: (_, r) => (
        <>
          <Progress percent={r.total_batches ? Math.round((r.completed / r.total_batches) * 100) : 0} size="small" strokeColor={C.green} showInfo={false} />
          <div style={{ fontSize: 10.5, color: C.sub }}>
            <b style={{ color: C.green }}>{r.completed}</b> done · <b style={{ color: C.blue }}>{r.running}</b> running
          </div>
        </>
      ),
    },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 92, render: (v) => <span style={{ fontSize: 11.5, color: C.sub }}>{fmtDate(v)}</span> },
    {
      title: '', key: 'a', width: 44, align: 'center',
      render: (_, r) => (
        <Popconfirm title="Delete this plan?" description="Completed batches keep their figures." okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(r.plan_id)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={S.page} className="mm-tight">
      {/* header + planner in ONE card */}
      <div style={{ ...S.card, marginBottom: 10 }}>
        <div style={{ ...S.pad, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: view === 'new' ? `1px solid ${C.line}` : 'none' }}>
          <div>
            <h1 style={S.h1}>Material Management</h1>
            <div style={S.sub}>What your batches need, what is in stock, what to buy · live from production, read-only</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="small" type={view === 'new' ? 'primary' : 'default'} icon={<PlusOutlined />} onClick={() => setView('new')}>New Plan</Button>
            <Button size="small" type={view !== 'new' ? 'primary' : 'default'} onClick={() => setView('list')}>Saved Plans</Button>
          </div>
        </div>

        {view === 'new' && (
          <div style={S.pad}>
            {items.map((it, idx) => {
              const sel = it.product_id ? productById(it.product_id) : null;
              return (
              <div key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 7, flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 150px' }}>
                  {idx === 0 && <span style={S.lbl}>Product — {familyOptions.length} with a BOM</span>}
                  <Select showSearch size="small" placeholder={productsLoading ? 'Loading…' : 'e.g. ATS'}
                    value={it.family}
                    onChange={(v) => patchItem(it.key, { family: v, product_id: null })}
                    loading={productsLoading} options={familyOptions} filterOption={filterProduct}
                    style={{ width: '100%' }} />
                </div>
                <div style={{ flex: '1 1 320px', maxWidth: 470, minWidth: 220 }}>
                  {idx === 0 && <span style={S.lbl}>BMR / BPR document</span>}
                  <Select showSearch size="small"
                    placeholder={it.family ? 'Select a document' : 'Pick a product first'}
                    disabled={!it.family}
                    value={it.product_id} onChange={(v) => patchItem(it.key, { product_id: v })}
                    options={docOptionsFor(it.family)} filterOption={filterProduct}
                    style={{ width: '100%' }} />
                  {/* Identifiers under the selector: the name alone is ambiguous
                      (many products share one) and the id is what you need to
                      query the database directly. */}
                  {sel && (
                    <div style={{ ...S.mono, fontSize: 10.5, color: C.sub, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span>product_id <b style={{ color: C.ink }}>{sel.id}</b></span>
                      {sel.product_family && <span>· family {sel.product_family}</span>}
                      <span>· {sel.name}</span>
                      {sel.bmr_bpr_code && sel.bmr_bpr_code !== 'N/A' && <span>· {sel.bmr_bpr_code}</span>}
                      {sel.bmr_bpr_type && <span>· {sel.bmr_bpr_type}</span>}
                      <span>· {sel.bom_material_count} materials</span>
                      {sel.theoretical_batch_size && <span>· {sel.theoretical_batch_size}</span>}
                    </div>
                  )}
                </div>
                <div>
                  {idx === 0 && <span style={S.lbl}>Batches</span>}
                  <InputNumber size="small" min={1} max={999} value={it.batches} onChange={(v) => patchItem(it.key, { batches: v || 1 })} style={{ width: 78 }} />
                </div>
                <Button size="small" danger type="text" icon={<DeleteOutlined />} disabled={items.length === 1} onClick={() => removeItem(it.key)} />
                {idx === items.length - 1 && (
                  <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addItem}>Add product</Button>
                )}
                {idx === 0 && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginLeft: 'auto' }}>
                    <div>
                      <span style={S.lbl}>Plan name (optional)</span>
                      <Input size="small" placeholder="e.g. October campaign" value={planName} onChange={(e) => setPlanName(e.target.value)} style={{ width: 170 }} />
                    </div>
                    <Button size="small" icon={<ReloadOutlined />} onClick={loadRequirements} loading={reqLoading} />
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => exportMaterials(combined, 'material-requirement')} disabled={!combined.length}>Export</Button>
                    <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!validItems.length}>Save Plan</Button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---------------- NEW PLAN ---------------- */}
      {view === 'new' && (
        !validItems.length ? (
          <div style={{ ...S.card, padding: 28 }}>
            <Empty description={<span style={{ fontSize: 12.5 }}>Pick a product above to see what material it needs</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <Spin spinning={reqLoading}>
            <StatusStrip materials={combined} batches={totalBatches} products={validItems.length}
              batchesPossible={batchesPossible}
              batchSize={validItems.length === 1 ? (productById(validItems[0].product_id)?.theoretical_batch_size || '—') : null} />
            <ShortList materials={combined} />
            <Warnings items={allWarnings} />
            <div style={{ ...S.card, ...S.pad }}>
              <Tabs size="small" items={[
                {
                  key: 'c', label: `Combined (${combined.length})`,
                  children: <>
                    <div style={{ ...S.hint, marginBottom: 8 }}>Required is added across products; stock counted once per material — it is the same physical stock.</div>
                    <MaterialTable rows={combined} emptyText="No BOM materials for the selected products" />
                  </>,
                },
                ...validItems.map((it) => {
                  const p = productById(it.product_id);
                  return {
                    key: String(it.product_id),
                    label: `${p?.code || p?.name?.slice(0, 16) || 'Product'} × ${it.batches}`,
                    children: <>
                      <div style={{ ...S.hint, marginBottom: 8 }}>
                        <b style={{ color: C.ink }}>{p?.name}</b> · {it.batches} batch{it.batches === 1 ? '' : 'es'} · theoretical batch size {p?.theoretical_batch_size || '—'}
                      </div>
                      <MaterialTable rows={results[it.product_id]?.materials} emptyText="This product has no BOM configured" />
                    </>,
                  };
                }),
              ]} />
            </div>
          </Spin>
        )
      )}

      {/* ---------------- PLAN LIST ---------------- */}
      {view === 'list' && (
        <div style={{ ...S.card, padding: 4 }}>
          <Table columns={planColumns} dataSource={plans.map((p) => ({ ...p, key: p.plan_id }))} loading={plansLoading}
            size="small" pagination={plans.length > 20 ? { pageSize: 20, size: 'small' } : false}
            locale={{
              emptyText: <div style={{ padding: 24 }}>
                <Empty description={<span style={{ fontSize: 12.5 }}>No plans saved yet</span>} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setView('new')}>Create your first plan</Button>
                </Empty>
              </div>,
            }} />
        </div>
      )}

      {/* ---------------- PLAN DETAIL ---------------- */}
      {view === 'detail' && (
        <Spin spinning={detailLoading}>
          {!detail ? <div style={{ ...S.card, padding: 28 }}><Empty description="Plan not found" image={Empty.PRESENTED_IMAGE_SIMPLE} /></div> : (
            <>
              <div style={{ ...S.card, ...S.pad, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{detail.plan_name || 'Untitled plan'}</div>
                  <div style={{ ...S.mono, fontSize: 10.5, color: C.mute }}>{detail.plan_id}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Stat label="Products" value={detail.summary?.products ?? '—'} />
                  <Stat label="Batches" value={detail.summary?.total_batches ?? '—'} />
                  <Stat label="Completed" value={detail.summary?.completed ?? '—'} color={C.green} />
                  <Stat label="Running" value={detail.summary?.running ?? '—'} color={C.blue} />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setView('list')}>Back</Button>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => openPlan(detail.plan_id)} />
                </div>
              </div>

              <Warnings items={detail.warnings} />

              <div style={{ ...S.card, ...S.pad }}>
                <Tabs size="small" items={[
                  {
                    key: 'live', label: `Requirement — ${detail.summary?.running ?? 0} running`,
                    children: !(detail.products || []).length
                      ? <Empty description={<span style={{ fontSize: 12.5 }}>Every batch is complete — nothing left to calculate</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      : detail.products.map((p) => (
                        <div key={p.product_id} style={{ marginBottom: 18 }}>
                          <div style={{ ...S.hint, marginBottom: 8 }}>
                            <b style={{ color: C.ink }}>{p.product_name}</b>
                            <span style={{ ...S.mono, color: C.mute }}> (product_id {p.product_id})</span>
                            {' · '}{p.running} running batch{p.running === 1 ? '' : 'es'}
                            {p.theoretical_batch_size ? ` · theoretical batch size ${p.theoretical_batch_size}` : ''} · recalculated live
                          </div>
                          <ShortList materials={p.live_materials} />
                          <Warnings items={p.warnings} />
                          <MaterialTable rows={p.live_materials} emptyText="This product has no BOM configured" />
                        </div>
                      )),
                  },
                  {
                    key: 'done', label: `Completed — ${detail.summary?.completed ?? 0}`,
                    children: !(detail.completed_batches || []).length
                      ? <Empty description={<span style={{ fontSize: 12.5 }}>No batches completed yet. A batch is frozen here once its material is dispensed.</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      : detail.completed_batches.map((b) => (
                        <div key={b.id} style={{ marginBottom: 18 }}>
                          <div style={{ ...S.hint, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                            <span style={{ ...S.mono, fontSize: 13, fontWeight: 700, color: C.ink }}>{b.product_batch_number}</span>
                            <Tag color="green" style={{ marginInlineEnd: 0 }}>Completed</Tag>
                            <span>{b.product_name} · figures locked {fmtDate(b.completed_date)}</span>
                          </div>
                          <MaterialTable rows={b.material_details} emptyText="No material detail stored" />
                        </div>
                      )),
                  },
                ]} />
              </div>
            </>
          )}
        </Spin>
      )}
    </div>
  );
};

export default MaterialManagement;
